import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Category } from '../types';
import { onImgError, proxiedImage } from '../utils/images';

interface CategoryCardProps {
  category: Category;
  index?: number;
  size?: 'small' | 'medium' | 'large';
  badge?: string;
}

const CategoryCard: React.FC<CategoryCardProps> = ({ category, index = 0, size = 'medium', badge }) => {
  const heights = { small: 'h-40', medium: 'h-52', large: 'h-64' };
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.5, delay: Math.min(index * 0.05, 0.3), ease: [0.4, 0, 0.2, 1] }}
    >
      <Link to={`/categories/${category.slug}`} className="block group organic-card overflow-hidden">
        <div className={`relative ${heights[size]} overflow-hidden bg-gradient-to-br from-sage-300 via-sage-200 to-cream-200`}>
          {category.image ? (
            <img src={proxiedImage(category.image)} alt={category.name} loading="lazy" decoding="async"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
              onError={onImgError()} />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center opacity-60">
              <svg className="h-16 w-16 text-sage-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 21c0-6 4-11 11-11 3 0 7 1 7 1s-1 4-1 7c0 4-3 7-7 7-3 0-6-1-8-4z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 21c4-4 8-7 14-9" />
              </svg>
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-earth-900/85 via-earth-900/30 to-transparent"></div>
          {badge && (
            <span className="absolute top-3 left-3 rounded-full bg-cream-50/90 backdrop-blur px-3 py-1 text-xs font-semibold text-earth-800 shadow-soft">{badge}</span>
          )}
          <div className="absolute bottom-0 left-0 right-0 p-5">
            <h3 className="font-display text-2xl font-semibold text-cream-50 leading-tight drop-shadow-md">{category.name}</h3>
            {size === 'large' && category.description && (
              <p className="text-sm text-cream-200 mt-1 line-clamp-2 leading-relaxed drop-shadow">{category.description}</p>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default CategoryCard;
